import { createContext, useContext, useEffect, useState, ReactNode } from 'react';
import { logger } from '@/lib/logger';

type Theme = 'light' | 'dark' | 'system';
type ResolvedTheme = 'light' | 'dark';

interface ThemeContextType {
  theme: Theme;
  resolvedTheme: ResolvedTheme;
  setTheme: (theme: Theme) => void;
  cycleTheme: () => void;
  label: string;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

const STORAGE_KEY = 'modcar-theme';

/**
 * Get system preference
 */
function getSystemTheme(): ResolvedTheme {
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/**
 * Resolve theme to actual light/dark
 */
function resolveTheme(theme: Theme): ResolvedTheme {
  return theme === 'system' ? getSystemTheme() : theme;
}

/**
 * Apply theme to document
 */
function applyTheme(resolved: ResolvedTheme) {
  const root = document.documentElement;
  root.classList.remove('light', 'dark');
  root.classList.add(resolved);

  // Update favicon to match theme (for in-app toggle, not just system preference)
  updateFavicon(resolved);
}

/**
 * Update favicon based on theme
 * This handles the case where user manually toggles theme (not just system preference)
 *
 * Chrome caches favicons aggressively, so we:
 * 1. Update ALL favicon links (including media query ones) to the same icon
 * 2. Add cache-busting query param to force reload
 * 3. Remove and re-add the link element to force Chrome to notice the change
 */
function updateFavicon(theme: ResolvedTheme) {
  if (typeof document === 'undefined') return;

  const faviconPath =
    theme === 'dark' ? '/modcar-logo-icon-dark.png' : '/modcar-logo-icon-lightblue.png';

  const cacheBuster = `?v=${Date.now()}`;
  const fullPath = `${faviconPath}${cacheBuster}`;

  // Update ALL icon links to override media query behavior when user manually toggles
  const allIconLinks = document.querySelectorAll('link[rel="icon"]');
  allIconLinks.forEach((link) => {
    const linkEl = link as HTMLLinkElement;
    // Remove media attribute to ensure this icon is used regardless of system preference
    linkEl.removeAttribute('media');
    linkEl.href = fullPath;
  });

  // Also update the apple-touch-icon
  const appleLink = document.querySelector('link[rel="apple-touch-icon"]');
  if (appleLink) {
    appleLink.href = fullPath;
  }
}

/**
 * Get theme label for UI
 */
function getThemeLabel(theme: Theme): string {
  switch (theme) {
    case 'light':
      return 'Licht';
    case 'dark':
      return 'Donker';
    case 'system':
      return 'Systeem';
  }
}

interface ThemeProviderProps {
  children: ReactNode;
  defaultTheme?: Theme;
}

/**
 * Central Theme Provider
 *
 * Features:
 * - Manages theme state (light/dark/system)
 * - Persists to localStorage
 * - Tracks OS changes in system mode
 * - Instant UI updates
 * - No FOUC (handled by inline script)
 *
 * @example
 * <ThemeProvider>
 *   <App />
 * </ThemeProvider>
 */
export function ThemeProvider({ children, defaultTheme = 'system' }: ThemeProviderProps) {
  const [theme, setThemeState] = useState<Theme>(() => {
    // Read from localStorage on mount
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (stored === 'light' || stored === 'dark' || stored === 'system') {
        return stored;
      }
    } catch (e) {
      logger.error('Failed to read theme from localStorage:', e);
    }
    return defaultTheme;
  });

  const [resolvedTheme, setResolvedTheme] = useState<ResolvedTheme>(() => {
    return resolveTheme(theme);
  });

  // Update resolved theme when theme changes
  useEffect(() => {
    const resolved = resolveTheme(theme);
    setResolvedTheme(resolved);
    applyTheme(resolved);

    // Persist to localStorage
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch (e) {
      logger.error('Failed to save theme to localStorage:', e);
    }
  }, [theme]);

  // Listen to system theme changes when in system mode
  useEffect(() => {
    if (theme !== 'system') return;

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

    const handleChange = (e: MediaQueryListEvent) => {
      const resolved = e.matches ? 'dark' : 'light';
      setResolvedTheme(resolved);
      applyTheme(resolved);
    };

    // Modern browsers
    mediaQuery.addEventListener('change', handleChange);

    return () => {
      mediaQuery.removeEventListener('change', handleChange);
    };
  }, [theme]);

  const setTheme = (newTheme: Theme) => {
    setThemeState(newTheme);
  };

  const cycleTheme = () => {
    const themes: Theme[] = ['light', 'dark', 'system'];
    const currentIndex = themes.indexOf(theme);
    const nextIndex = (currentIndex + 1) % themes.length;
    setTheme(themes[nextIndex]);
  };

  const label = getThemeLabel(theme);

  const value: ThemeContextType = {
    theme,
    resolvedTheme,
    setTheme,
    cycleTheme,
    label,
  };

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * Hook to access theme context
 *
 * @example
 * const { theme, resolvedTheme, setTheme, cycleTheme, label } = useTheme();
 */
export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
